import React, { useEffect, useState } from "react";
import { useLanguage } from "@/i18n/LanguageContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Calendar as CalendarIcon,
  Clock,
  Users,
  ArrowRight,
  CheckCircle2,
} from "lucide-react";
import { createReservation, getTimeSlots } from "@/lib/api";
import { format } from "date-fns";
import { PageHeaderBanner } from "@/components/PageHeaderBanner";

const fieldClass =
  "w-full bg-transparent border-0 border-b border-border rounded-none px-0 py-3 focus-visible:ring-0 focus-visible:border-primary transition-colors text-base placeholder:text-foreground/40";

const triggerClass =
  "w-full justify-between bg-transparent border-0 border-b border-border rounded-none px-0 py-3 h-auto text-left focus:ring-0 focus:border-primary text-base font-normal";

const guestOptions = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

const Reserve = () => {
  const { t, lang } = useLanguage();
  const [date, setDate] = useState(null);
  const [time, setTime] = useState("");
  const [guests, setGuests] = useState("2");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [slots, setSlots] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [openDate, setOpenDate] = useState(false);

  useEffect(() => {
    let mounted = true;
    getTimeSlots()
      .then((s) => {
        if (mounted) setSlots(s);
      })
      .catch(() => {
        if (mounted) {
          const fallback = [];
          for (let h = 12; h < 22; h++) {
            fallback.push(`${String(h).padStart(2, "0")}:00`);
            fallback.push(`${String(h).padStart(2, "0")}:30`);
          }
          fallback.push("22:00");
          setSlots(fallback);
        }
      });
    return () => {
      mounted = false;
    };
  }, []);

  const reset = () => {
    setDate(null);
    setTime("");
    setGuests("2");
    setName("");
    setPhone("");
    setEmail("");
    setMessage("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!date || !time || !guests || !name.trim() || !phone.trim() || !email.trim()) {
      toast.error(t("reserve.form.error"));
      return;
    }
    try {
      setSubmitting(true);
      await createReservation({
        name: name.trim(),
        phone: phone.trim(),
        email: email.trim(),
        date: format(date, "yyyy-MM-dd"),
        time,
        guests: Number(guests),
        message: message.trim() || undefined,
        language: lang,
      });
      setSuccess(true);
      reset();
    } catch (err) {
      toast.error(t("reserve.form.error"));
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div data-testid="reserve-success" className="pt-40 pb-32 bg-sky-roze paper-grain min-h-screen">
        <div className="container-roze max-w-2xl text-center">
          <CheckCircle2 className="h-12 w-12 text-gold mx-auto mb-8" strokeWidth={1.4} />
          <div className="overline mb-5">{t("reserve.eyebrow")}</div>
          <h1 className="font-serif text-5xl md:text-6xl font-light leading-[1.02] tracking-tight italic">
            {t("reserve.form.success_title")}
          </h1>
          <p className="mt-6 text-lg text-foreground/70 leading-relaxed">
            {t("reserve.form.success_body")}
          </p>
          <button
            type="button"
            data-testid="reserve-success-new"
            onClick={() => setSuccess(false)}
            className="mt-10 inline-flex items-center gap-3 px-8 py-4 border border-gold/60 text-foreground hover:bg-white/60 transition-colors text-xs uppercase tracking-[0.22em]"
          >
            {t("nav.reserve")}
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="reserve-page" className="pb-24 bg-sky-roze paper-grain relative overflow-hidden">
      <PageHeaderBanner
        testid="reserve-header-banner"
        eyebrow={t("reserve.eyebrow")}
        title={t("reserve.title")}
        lead={t("reserve.lead")}
      />

      <section className="container-roze grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-20 relative pt-16 md:pt-24">
        <div className="md:col-span-5 animate-fade-up">
          <div className="border-t border-gold/40 pt-8 space-y-3 text-sm text-foreground/75">
            <div className="flex items-center gap-3">
              <Clock className="h-4 w-4 text-gold" strokeWidth={1.4} />
              <span>{t("contact.hours_value")}</span>
            </div>
            <div className="flex items-center gap-3">
              <Users className="h-4 w-4 text-gold" strokeWidth={1.4} />
              <span>{t("reserve.form.select_guests")}</span>
            </div>
          </div>
        </div>

        <div className="md:col-span-7">
          <div className="bg-white/70 border border-gold/40 p-8 md:p-10 paper-grain">
            <form
              data-testid="reserve-form"
              onSubmit={handleSubmit}
              className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-7"
            >
            {/* Date */}
            <div className="md:col-span-1">
              <label className="overline block mb-3">{t("reserve.form.date")}</label>
              <Popover open={openDate} onOpenChange={setOpenDate}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    data-testid="reserve-date-trigger"
                    className={`${triggerClass} flex items-center gap-3 ${
                      date ? "text-foreground" : "text-foreground/40"
                    }`}
                  >
                    <CalendarIcon className="h-4 w-4 text-primary" strokeWidth={1.4} />
                    {date ? format(date, "PPP") : t("reserve.form.select_date")}
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  align="start"
                  className="p-0 rounded-none border-border"
                  data-testid="reserve-date-popover"
                >
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={(d) => {
                      setDate(d);
                      setOpenDate(false);
                    }}
                    disabled={(d) => {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      return d < today;
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Time */}
            <div className="md:col-span-1">
              <label className="overline block mb-3">{t("reserve.form.time")}</label>
              <Select value={time} onValueChange={setTime}>
                <SelectTrigger
                  data-testid="reserve-time-trigger"
                  className={triggerClass}
                >
                  <div className="flex items-center gap-3 w-full">
                    <Clock className="h-4 w-4 text-primary" strokeWidth={1.4} />
                    <SelectValue placeholder={t("reserve.form.select_time")} />
                  </div>
                </SelectTrigger>
                <SelectContent className="rounded-none border-border max-h-72">
                  {slots.map((s) => (
                    <SelectItem
                      key={s}
                      value={s}
                      data-testid={`reserve-time-option-${s}`}
                      className="rounded-none"
                    >
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Guests */}
            <div className="md:col-span-2">
              <label className="overline block mb-3">{t("reserve.form.guests")}</label>
              <Select value={guests} onValueChange={setGuests}>
                <SelectTrigger
                  data-testid="reserve-guests-trigger"
                  className={triggerClass}
                >
                  <div className="flex items-center gap-3 w-full">
                    <Users className="h-4 w-4 text-primary" strokeWidth={1.4} />
                    <SelectValue placeholder={t("reserve.form.select_guests")} />
                  </div>
                </SelectTrigger>
                <SelectContent className="rounded-none border-border">
                  {guestOptions.map((g) => (
                    <SelectItem
                      key={g}
                      value={String(g)}
                      data-testid={`reserve-guests-option-${g}`}
                      className="rounded-none"
                    >
                      {g}
                    </SelectItem>
                  ))}
                  <SelectItem value="11" data-testid="reserve-guests-option-11" className="rounded-none">
                    10+
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Name */}
            <div className="md:col-span-1">
              <label className="overline block mb-2">{t("reserve.form.name")}</label>
              <Input
                data-testid="reserve-input-name"
                className={fieldClass}
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>

            {/* Phone */}
            <div className="md:col-span-1">
              <label className="overline block mb-2">{t("reserve.form.phone")}</label>
              <Input
                data-testid="reserve-input-phone"
                className={fieldClass}
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
              />
            </div>

            {/* Email */}
            <div className="md:col-span-2">
              <label className="overline block mb-2">{t("reserve.form.email")}</label>
              <Input
                type="email"
                data-testid="reserve-input-email"
                className={fieldClass}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            {/* Message */}
            <div className="md:col-span-2">
              <label className="overline block mb-2">{t("reserve.form.message")}</label>
              <Textarea
                data-testid="reserve-input-message"
                className={`${fieldClass} min-h-[120px] resize-none`}
                placeholder={t("reserve.form.message_placeholder")}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
            </div>

            <div className="md:col-span-2 mt-4">
              <button
                type="submit"
                data-testid="reserve-submit"
                disabled={submitting}
                className="group inline-flex items-center gap-3 px-10 py-4 bg-primary text-primary-foreground hover:bg-primary/90 transition-colors text-xs uppercase tracking-[0.22em] disabled:opacity-50"
              >
                {submitting ? t("reserve.form.submitting") : t("reserve.form.submit")}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" strokeWidth={1.5} />
              </button>
            </div>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Reserve;
